<template>
  <div>
    <h4 class="text-exitos mb-1">
      <font-awesome-icon icon="earth-americas" />
      Território internacional
    </h4>
    <p v-text="hintCount" class="text-muted mb-1 pl-1" />

    <tree-select
      v-model="modelValue"
      :disabled="isDisabled"
      :options="countries"
      :placeholder="placeholder"
      :normalizer="normalizer"
      flat
      multiple
      no-options-text="Nenhum país disponível."
      no-results-text="Nenhum país encontrado."
    />

    <div
      v-if="loading"
      class="d-flex align-items-center mt-2 text-muted small"
      role="status"
    >
      <b-spinner small class="mr-2" />
      Carregando países...
    </div>

    <b-alert v-else-if="isError" show variant="danger" class="mt-2 mb-0">
      Não foi possível carregar os países.
      <b-button
        type="button"
        variant="link"
        class="p-0 align-baseline"
        @click="refetch"
      >
        Tentar novamente
      </b-button>
    </b-alert>
  </div>
</template>

<script>
import { computed, defineComponent, watchEffect } from "vue";
import services from "@/http";
import { useQuery } from "@/composables/useQuery.js";
import {
  PluckItem,
  safeArray,
  size,
} from "@/helpers/functions/ArrayHelpers.js";
import { useVModel } from "@vueuse/core";
import { deepCopy } from "@/helpers/functions/ObjectHelpers";

export default defineComponent({
  name: "SelectCountry",
  props: {
    value: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["input"],
  setup(props, { emit }) {
    const {
      data: countries,
      isFetching: loading,
      isError,
      refetch,
    } = useQuery({
      key: ["select-countries"],
      queryFn: () => services.municipios.to_select_paises(),
      initialData: () => [],
      shallow: true,
      select: (response) =>
        safeArray(response.data).map((option) => ({
          label: option.text,
          value: option.value,
          id: option.value,
        })),
    });

    const modelValue = computed({
      get() {
        return safeArray(props.value).map(countryCode).filter(Boolean);
      },
      set(value) {
        emit("input", safeArray(value).map(countryCode).filter(Boolean));
      },
    });

    const isDisabled = computed(() => loading.value || isError.value);

    const placeholder = computed(() =>
      loading.value ? "Carregando países..." : "Selecione os países..."
    );

    const count = computed(() => size(modelValue.value));

    const countCompare = computed(() =>
      Math.max(size(countries.value) - count.value, 0)
    );

    const hintCount = computed(() =>
      count.value === 1
        ? `${count.value} País selecionado de ${countCompare.value} disponíveis`
        : `${count.value} Países selecionados de ${countCompare.value} disponíveis`
    );

    function countryCode(country) {
      if (typeof country === "string") return country;

      return country?.iso3 ?? country?.value ?? country?.id ?? null;
    }

    function normalizer(country) {
      return {
        id: country.value ?? country.iso3 ?? country.id,
        label: country.text ?? country.nome ?? country.label ?? country.id,
      };
    }

    return {
      countries,
      isError,
      isDisabled,
      loading,
      modelValue,
      placeholder,
      hintCount,
      countCompare,
      normalizer,
      refetch,
    };
  },
});
</script>
